import type { FieldRenderProps, Form } from 'react-final-form';
import type { ReactNode } from 'react';
import { FORM_ERROR } from 'final-form';

import { formatNumber } from '../helpers/formatting';
import { getPropertyAccessorFromObjectString } from '../components/AdForm/helpers/adFormMethods';
import { PriceTypes } from '../components/AdForm/types/index';

export type FormSubmissionResult = ReturnType<
  Parameters<typeof Form>[0]['onSubmit']
>;

export function handleErrorMessage(meta: FieldRenderProps<unknown>['meta']) {
  const touched = meta.touched;
  const hasError =
    (meta.error && !meta.dirtySinceLastSubmit) ||
    (meta.submitError && !meta.dirtySinceLastSubmit);
  return (
    (touched && hasError && meta.error) ||
    (touched && hasError && meta.submitError)
  );
}

export function handleErrorMessageOnBlur(
  meta: FieldRenderProps<unknown>['meta'],
) {
  const touched = meta.touched;
  return (touched && meta.error) || (touched && meta.submitError);
}

export function handleErrorMessageOnChange(
  meta: FieldRenderProps<unknown>['meta'],
) {
  return meta.error || meta.submitError;
}

type ErrorObject422 = {
  field: string;
  message: string;
};

type NestedFieldErrors = Record<string, string>;

export type FormattedErrors = {
  attributes?: NestedFieldErrors;
} & Record<string, string | NestedFieldErrors | undefined>;

export function format422ErrorsFromApiForForm(
  errors: ErrorObject422[],
): FormattedErrors {
  const deDuplicatedArray = [...new Set(errors)];

  return deDuplicatedArray.reduce<FormattedErrors>((acc, currentError) => {
    /* adAttribute 422 errors are returned with field name formatted as
      'adAttributes[fieldName]. This must be reformatted as follows for React Final Form:
      {
         attributes: {
          fieldName: value,
         },
       }; */
    if (currentError.field.indexOf('[') !== -1) {
      /* Retrieve field name found between brackets where the field name
        is in the format 'attributes[${fieldName}]' */
      const fieldName = getPropertyAccessorFromObjectString(currentError.field);
      acc.attributes = {
        ...(acc.attributes ?? {}),
        [fieldName]: currentError.message,
      };
    } else if (currentError.field.indexOf('.') !== -1) {
      const [head, tail] = currentError.field.split('.');
      const existing = acc[head];
      const base =
        typeof existing === 'object' && existing !== null ? existing : {};
      acc[head] = { ...base, [tail]: currentError.message };
    } else {
      acc[currentError.field] = currentError.message;
    }
    return acc;
  }, {});
}

export function getFormFieldErrorMessage(
  error: string | { [key: string]: string } | undefined,
): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    return Object.values(error)[0] ?? '';
  }
  return '';
}

export function formatFormSubmissionError(errorMessage: string | ReactNode) {
  return { [FORM_ERROR]: errorMessage };
}

export function removeNonNumericCharacters(value?: string | number) {
  if (value === PriceTypes.ON_REQUEST) return value;
  if (value === undefined) return '';
  return String(value).replace(/\D/g, '');
}

export function formatFormNumber(value?: string | number) {
  if (value === PriceTypes.ON_REQUEST) return String(value);
  if (value === undefined || value === '') return '';
  const numValue =
    typeof value === 'string'
      ? Number.parseFloat(value.replace(/,/g, ''))
      : value;
  return Number.isNaN(numValue)
    ? String(value)
    : String(formatNumber(numValue));
}

// https://github.com/final-form/react-final-form/issues/130#issuecomment-425482365
export function keepEmptyStringAsFormValue(value: string) {
  return value;
}

/**
 * When an invalid car reg is submitted and the user continues to submit the ad, 422 errors are returned from BE.
 * We pass up attributes[registrationNumber] and BE expects required field values for vehicle make and year, so will return this error:
 * [{
 *  code: 0
 *  field: "adAttributes[make]"
 *  message: "Please enter the vehicle make."
 * },
 * {
 * code: 0
 * field: "adAttributes[year]"
 * message: "Please enter the vehicle year"
 * }]
 * As these fields are not displayed we instead should return a field error for the reglookup.
 */
export function mapVehicleAttributeErrorsToRegLookupError(
  formattedErrors: FormattedErrors,
  manualInputView: boolean,
) {
  if (!manualInputView && formattedErrors.attributes?.make) {
    return {
      ...formattedErrors,
      attributes: {
        ...formattedErrors.attributes,
        registrationNumber: 'Please enter a valid registration',
      },
    };
  }

  return formattedErrors;
}

/**
 * Will prevent default behaviour of form submission when pressing `Enter` if any form input field is active.
 * @param event passed from onKeyPress form element prop
 */
export function preventActiveInputFormSubmit(event: React.KeyboardEvent) {
  if (
    event.key === 'Enter' &&
    (event.target as Element)?.nodeName === 'INPUT'
  ) {
    event.preventDefault();
  }
}
